import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ActionDialog, Alert, Copy, Field, Input, Modal, Select } from '@hexure/ui';
import { mdiFileDownloadOutline, mdiEmailOutline, mdiForumOutline, mdiChevronRight, mdiCheckboxMarkedCircleOutline, mdiOpenInNew } from '@mdi/js';

import ActionCard from 'shared/ActionCard';

import { ThemeContext } from 'shared/ThemeContext';

import ApiUtils from 'utils/AnnuitiesApi';
import FormUtils from 'utils/Form';
import { getAgentInfo } from 'utils/AgentInfo';
import { FontWeights } from 'constants/Clementine';

const QUOTER_CONFIG = window.HEXURE_UQ_CONFIG || {};

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 40px;
  justify-content: center;
`;

const Layouts = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2px;
  justify-items: center;
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  cursor: pointer;
  width: 100%;
  max-width: 400px;
`;

const PdfPreview = styled.div`
  width: 400px;
  height: 280px;
  border-radius: 8px;
  border-width: 2px;
  border-style: solid;
  border-color: ${props => (props.$isSelected ? props.$color : '#fff')};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url(${props => props.$image});
  position: relative;

  &::after {
    content: 'Example Only';
    position: absolute;
    top: 0;
    right: 0;
    background: ${props => (props?.$isSelected ? props.$color : '#fff')};
    padding: 5px 10px;
    border-width: 0.5px;
    border-style: solid;
    border-color: ${props => props?.$color};
    border-radius: 0 0 0 8px;
    font-size: 10px;
    color: ${props => (props?.$isSelected ? '#fff' : props?.$color)};
    font-weight: ${FontWeights?.BOLD};
    overflow: hidden;
  }
`;

const InfoContent = styled.div`
  width: 800px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Form = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
`;

const Column = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FieldGroup = styled.div`
  display: flex;
  padding: 18px 18px 0;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  background: #fcfcfc;
`;

const Row = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
`;

const ComparisonModal = ({ agent, client, onClose, selectedQuotes, isCalifornia = false }) => {
  const { color } = useContext(ThemeContext);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const agentInfo = (QUOTER_CONFIG?.embed_context === 'fire_light') ? getAgentInfo() : null;
  const [data, setData] = useState({
    delivery_method: 'text',
    template: 'demo',
    agent_email: agentInfo ? agentInfo?.email || '' : agent?.email || '',
    agent_first_name: agentInfo ? agentInfo?.first_name || '' : agent?.first_name || '',
    agent_last_name: agentInfo ? agentInfo?.last_name || '' : agent?.last_name || '',
    agent_phone: agentInfo ? agentInfo?.phone || '' : agent?.phone || '',
    agent_license: agent?.license || '',
    agent_california_license: '',
    first_name: client?.first_name || '',
    last_name: client?.last_name || '',
    quote_guids: selectedQuotes,
    phone: '',
    email: '',
    subject: 'Your Annuities Quote',
    message: 'Thank you for your interest! Take a look at your personalized annuities quote.'
  });
  const [page, setPage] = useState('delivery_method');
  const [pdf_url, setPdfUrl] = useState(null);
  const titles = {
    delivery_method: 'Select a Delivery Method',
    template: 'Select a PDF Layout',
    information: 'Enter Information'
  };
  const setDeliveryMethod = delivery_method => {
    setData({
      ...data,
      delivery_method
    });
  };
  const setLayout = template => {
    setData({
      ...data,
      template
    });
  };
  const handleInputChange = (name, e) => {
    setData({
      ...data,
      [name]: e.target.value
    });
  };
  const handlePdfTemplate = (field, selectedValue) => {
    setData(prevData => ({
      ...prevData,
      [field]: selectedValue
    }));
  };
  const handleSubmit = () => {
    const new_errors = [];
    const { phone, email, subject, message, delivery_method, first_name, last_name, ...payload } = data;

    //to save quote in firelight
    if (QUOTER_CONFIG?.embed_context === 'fire_light' && typeof window.saveQuote === 'function') {
      window.saveQuote();
    }

    if (delivery_method === 'email') {
      if (!FormUtils._validateEmail(payload.agent_email)) {
        new_errors.push('agent_email');
      }

      if (!FormUtils._validateEmail(email)) {
        new_errors.push('email');
      }

      if (!subject) {
        new_errors.push('subject');
      }

      if (!message) {
        new_errors.push('message');
      }
    }

    if (delivery_method === 'text') {
      if (!FormUtils._validatePhone(phone)) {
        new_errors.push('phone');
      }
      if (!message) {
        new_errors.push('message');
      }
    }

    if (new_errors.length) {
      setErrors(new_errors);
    } else {
      setLoading(true);

      if (delivery_method === 'email') {
        payload.from = payload.agent_email;
        payload.to = email;
        payload.subject = subject;
        payload.message = message;
      }

      if (payload.delivery_method === 'text') {
        payload.phone_number = phone;
        payload.message = message;
      }

      ApiUtils.generateQuotesPdf({
        ...payload,
        client_name: `${first_name} ${last_name}`,
        pdf_action: delivery_method,
        phone_number: data?.phone || '',
        message: data?.message || '',
        show_agent_info: true
      })
        .then(({ data }) => {
          setLoading(false);
          setPdfUrl(data.url);
          setPage('success');
        })
        .catch(() => {
          setErrors(['api_error']);
          setTimeout(() => {
            setErrors([]);
          }, 3000);
        });
    }
  };
  const buttons = {
    delivery_method: {
      secondaryButton: {
        children: 'Cancel',
        onClick: onClose
      },
      primaryButton: {
        children: 'Next',
        icon: mdiChevronRight,
        onClick: setPage.bind(null, 'template')
      }
    },
    template: {
      secondaryButton: {
        children: 'Back',
        onClick: setPage.bind(null, 'delivery_method')
      },
      primaryButton: {
        children: 'Next',
        icon: mdiChevronRight,
        onClick: setPage.bind(null, 'information')
      }
    },
    information: {
      secondaryButton: {
        children: 'Back',
        disabled: loading,
        onClick: setPage.bind(null, 'template')
      },
      primaryButton: {
        children: data.delivery_method === 'download' ? `${loading ? 'Creating PDF...' : 'Create PDF'}` : `${loading ? 'Sending...' : 'Send'}`,
        icon: mdiCheckboxMarkedCircleOutline,
        loading,
        onClick: handleSubmit
      }
    }
  };

  return page === 'success' ? (
    <ActionDialog
      description={
        data.delivery_method === 'download'
          ? 'Your quote comparison PDF is ready. Click the button below to download it.'
          : 'Your quote comparison PDF has been sent. Click the button below to view the PDF that was sent.'
      }
      primaryButton={{
        children: 'View PDF',
        icon: mdiOpenInNew,
        onClick: () => window.open(pdf_url)
      }}
      secondaryButton={{
        children: 'Back to Results',
        onClick: onClose
      }}
      title='Success!'
    />
  ) : (
    <Modal onClose={onClose} title={titles[page]} {...buttons[page]} id='delivery-method-modal'>
      {page === 'delivery_method' ? (
        <Actions>
          <ActionCard icon={mdiForumOutline} isSelected={data.delivery_method === 'text'} onClick={setDeliveryMethod.bind(null, 'text')} id='text-pdf-action'>
            Text PDF
          </ActionCard>
          <ActionCard icon={mdiEmailOutline} isSelected={data.delivery_method === 'email'} onClick={setDeliveryMethod.bind(null, 'email')} id='email-pdf-action'>
            Email PDF
          </ActionCard>
          <ActionCard icon={mdiFileDownloadOutline} isSelected={data.delivery_method === 'download'} onClick={setDeliveryMethod.bind(null, 'download')} id='download-pdf-action'>
            Download PDF
          </ActionCard>
        </Actions>
      ) : null}

      {page === 'template' ? (
        <Layouts>
          <Layout $color={color} onClick={setLayout.bind(null, 'demo')} id='demo-pdf-template'>
            <Copy type='bold'>Summary Report</Copy>
            <PdfPreview $color={color} $image='https://ixntestsa001.blob.core.windows.net/ixn/images/standard.png' $isSelected={data.template === 'demo'} id='income-comparison-report-pdf' />
          </Layout>
          <Layout $color={color} onClick={setLayout.bind(null, 'annuity_quoter_accumulation')} id='accumulation-pdf-template'>
            <Copy type='bold'>Comprehensive Report</Copy>
            <PdfPreview
              $color={color}
              $image='https://ixntestsa001.blob.core.windows.net/ixn/images/accumulation-comparison-report.png'
              $isSelected={data.template === 'annuity_quoter_accumulation'}
              id='accumulation-report-pdf'
            />
          </Layout>
        </Layouts>
      ) : null}

      {page === 'information' ? (
        <InfoContent>
          {errors.includes('api_error') ? (
            <Alert
              description='An error occurred while trying to generate the PDF. If it continues to happen, please contact support.'
              title='Something went wrong'
              type='error'
              id='alert-error-message'
            />
          ) : null}
          <Form>
            <Column>
              <Copy type='bold'>Agent Information</Copy>
              <FieldGroup>
                <Row>
                  <Field label='First Name' style={{ width: 120 }} id='agent-information-first-name'>
                    <Input onChange={handleInputChange.bind(null, 'agent_first_name')} readOnly={loading} value={data.agent_first_name} id='agent-information-first-name-input' />
                  </Field>
                  <Field label='Last Name' style={{ flex: 1 }} id='agent-information-last-name'>
                    <Input onChange={handleInputChange.bind(null, 'agent_last_name')} readOnly={loading} value={data.agent_last_name} id='agent-information-last-name-input' />
                  </Field>
                </Row>
                <Field label='Email' required={data.delivery_method === 'email'} style={{ width: '100%' }} id='agent-information-email'>
                  <Input
                    invalid={errors.includes('agent_email')}
                    onChange={handleInputChange.bind(null, 'agent_email')}
                    readOnly={loading}
                    value={data.agent_email}
                    id='agent-information-email-input'
                  />
                </Field>
                <Field label='Phone' style={{ width: '100%' }} id='agent-information-phone'>
                  <Input format='phone' onChange={handleInputChange.bind(null, 'agent_phone')} readOnly={loading} value={data.agent_phone} id='agent-information-phone-input' />
                </Field>
                <Field label='License Number' style={{ width: '100%' }} id='agent-information-license'>
                  <Input onChange={handleInputChange.bind(null, 'agent_license')} readOnly={loading} value={data.agent_license} id='agent-information-license-input' />
                </Field>
                {isCalifornia ? (
                  <Field label='California License Number' style={{ width: '100%' }} id='agent-information-california-license'>
                    <Input onChange={handleInputChange.bind(null, 'agent_california_license')} readOnly={loading} value={data.agent_california_license} id='agent-information-california-license-input' />
                  </Field>
                ) : null}
              </FieldGroup>
            </Column>
            <Column>
              <Copy type='bold'>Recipient Information</Copy>
              <FieldGroup>
                <Row>
                  <Field label='First Name' style={{ width: 120 }} id='recipient-information-first-name'>
                    <Input onChange={handleInputChange.bind(null, 'first_name')} readOnly={loading} value={data.first_name} id='recipient-information-first-name-input' />
                  </Field>
                  <Field label='Last Name' style={{ flex: 1 }} id='recipient-information-last-name'>
                    <Input onChange={handleInputChange.bind(null, 'last_name')} readOnly={loading} value={data.last_name} id='recipient-information-last-name-input' />
                  </Field>
                </Row>
                <Field label='Select Pdf Template' required={true} style={{ width: '100%' }} id='select-pdf-template'>
                  <Select
                    onChange={e => handlePdfTemplate('template', e.target.value)}
                    options={[
                      { label: 'demo', value: 'demo' },
                      { label: 'annuity_quoter_accumulation', value: 'annuity_quoter_accumulation' },
                      { label: 'annuity_quoter_pages', value: 'annuity_quoter_pages' }
                    ]}
                    required={true}
                    value={data.template}
                    id='select-pdf-template-select'
                  />
                </Field>
                {data.delivery_method === 'email' ? (
                  <>
                    <Field label='Email' required={true} style={{ width: '100%' }} id='recipient-information-email'>
                      <Input invalid={errors.includes('email')} onChange={handleInputChange.bind(null, 'email')} readOnly={loading} value={data.email} id='recipient-information-email-input' />
                    </Field>
                    <Field label='Subject' required={true} style={{ width: '100%' }} id='recipient-information-subject'>
                      <Input invalid={errors.includes('subject')} onChange={handleInputChange.bind(null, 'subject')} readOnly={loading} value={data.subject} id='recipient-information-subject-input' />
                    </Field>
                    <Field label='Message' required={true} style={{ width: '100%' }} id='recipient-information-message'>
                      <Input
                        height={140}
                        invalid={errors.includes('message')}
                        onChange={handleInputChange.bind(null, 'message')}
                        readOnly={loading}
                        style={{ width: '100%' }}
                        type='textarea'
                        value={data.message}
                        id='recipient-information-message-input'
                      />
                    </Field>
                  </>
                ) : null}

                {data.delivery_method === 'text' ? (
                  <>
                    <Field label='Phone' required={true} style={{ width: '100%' }} id='recipient-information-phone'>
                      <Input
                        format='phone'
                        invalid={errors.includes('phone')}
                        onChange={handleInputChange.bind(null, 'phone')}
                        readOnly={loading}
                        value={data.phone}
                        id='recipient-information-phone-input'
                      />
                    </Field>
                    <Field label='Message' required={true} style={{ width: '100%' }} id='recipient-information-message'>
                      <Input
                        height={140}
                        invalid={errors.includes('message')}
                        onChange={handleInputChange.bind(null, 'message')}
                        readOnly={loading}
                        style={{ width: '100%' }}
                        type='textarea'
                        value={data.message}
                        id='recipient-information-message-input'
                      />
                    </Field>
                  </>
                ) : null}
              </FieldGroup>
            </Column>
          </Form>
        </InfoContent>
      ) : null}
    </Modal>
  );
};

ComparisonModal.propTypes = {
  agent: PropTypes.object,
  client: PropTypes.object,
  solve: PropTypes.object,
  onClose: PropTypes.func,
  selectedQuotes: PropTypes.array,
  isCalifornia: PropTypes.bool
};

export default ComparisonModal;
